import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

const addRetryQuery = (inputSrc, attempt) => {
  try {
    const parsed = new URL(inputSrc, window.location.href);
    parsed.searchParams.set("retryAttempt", attempt);
    return parsed.toString();
  } catch (error) {
    const separator = inputSrc.includes("?") ? "&" : "?";
    return `${inputSrc}${separator}retryAttempt=${attempt}`;
  }
};

const Image = ({
  src,
  alt,
  loader,
  unloader,
  container,
  style,
  retryOnError = false,
  retryMaxAttempts = 0,
  retryDelayMs = 0,
  onRetryStateChange,
}) => {
  const normalizedSrc = useMemo(
    () => (Array.isArray(src) ? src[0] : src),
    [src]
  );
  const canRetry = retryOnError && retryMaxAttempts > 0 && retryDelayMs > 0;
  const retryTimeoutRef = useRef(null);
  const [error, setError] = useState(!normalizedSrc);
  const [isLoading, setIsLoading] = useState(Boolean(normalizedSrc));
  const [attempt, setAttempt] = useState(0);
  const [isRetrying, setIsRetrying] = useState(false);
  const [currentSrc, setCurrentSrc] = useState(normalizedSrc);

  // Keep the latest callback in a ref so the notification effect below does
  // not depend on the callback's identity. Otherwise parents passing an
  // inline function would cause the effect to re-run on every parent render,
  // which can trigger an infinite update loop when the callback sets state.
  const onRetryStateChangeRef = useRef(onRetryStateChange);
  useEffect(() => {
    onRetryStateChangeRef.current = onRetryStateChange;
  }, [onRetryStateChange]);

  useEffect(() => {
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
    }
    setError(!normalizedSrc);
    setIsLoading(Boolean(normalizedSrc));
    setAttempt(0);
    setIsRetrying(false);
    setCurrentSrc(normalizedSrc);
  }, [normalizedSrc]);

  useEffect(() => {
    return () => {
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const cb = onRetryStateChangeRef.current;
    if (!cb) {
      return;
    }

    cb({
      attempt,
      hasExhausted:
        !isRetrying && error && canRetry && attempt >= retryMaxAttempts,
      isRetrying,
      maxAttempts: retryMaxAttempts,
    });
  }, [attempt, canRetry, error, isRetrying, retryMaxAttempts]);

  const handleError = useCallback(() => {
    if (!currentSrc) {
      setIsLoading(false);
      setError(true);
      setIsRetrying(false);
      return;
    }

    if (canRetry && attempt < retryMaxAttempts) {
      const nextAttempt = attempt + 1;

      setIsRetrying(true);
      setIsLoading(true);
      setError(false);
      setAttempt(nextAttempt);

      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }

      retryTimeoutRef.current = setTimeout(() => {
        setCurrentSrc(addRetryQuery(normalizedSrc, nextAttempt));
      }, retryDelayMs);
      return;
    }

    setIsRetrying(false);
    setIsLoading(false);
    setError(true);
  }, [
    attempt,
    canRetry,
    currentSrc,
    normalizedSrc,
    retryDelayMs,
    retryMaxAttempts,
  ]);

  const handleLoad = useCallback(() => {
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
    }
    setIsLoading(false);
    setError(false);
    setIsRetrying(false);
  }, []);

  const image = useMemo(
    () => (
      <img
        alt={alt}
        src={currentSrc}
        style={style}
        onError={handleError}
        onLoad={handleLoad}
        {...((isLoading || error) && { style: { display: "none", ...style } })}
      />
    ),
    [alt, currentSrc, handleError, handleLoad, style, isLoading, error]
  );

  return (
    <>
      {isLoading && loader}
      {error && unloader}
      {typeof container === "function" ? container(image) : image}
    </>
  );
};

export default memo(Image);
